import { Employee, Property } from './property-list.models';
import { normalizePhoneSearchValue, normalizeSearchText } from '../utils/search-normalization.utils';

export type VisibleEmployeeSearchField = 'name' | 'email' | 'phone' | 'mobile';

export interface PropertyListSearchConfiguration {
  hospitalityMode?: boolean;
  includeHausCode?: boolean;
  propertyManagerFields?: VisibleEmployeeSearchField[];
  technicalAssetsManagerFields?: VisibleEmployeeSearchField[];
  sevManagerFields?: VisibleEmployeeSearchField[];
  accountantFields?: VisibleEmployeeSearchField[];
  technicalServiceTypes?: string[];
}

const HOSPITALITY_SEARCH_EXCLUDED_KEYS = new Set([
  'id',
  '_id',
  'entryId',
  'companyEntryIds',
  'order',
  'createdAt',
  'updatedAt',
  'appliedAt',
  'deletedAt',
  'isDeleted',
  'isSoftDeleted',
  'isDuplicate',
  'source',
  'selectedCompanyHouseRuleIds',
  'selectedCompanyApplianceGuideIds',
  'selectedCompanyCheckOutItemIds',
]);

function collectHospitalityStrings(value: unknown, target: string[]): void {
  if (value == null) {
    return;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    if (normalized) {
      target.push(normalized);
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach(item => collectHospitalityStrings(item, target));
    return;
  }

  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
      if (HOSPITALITY_SEARCH_EXCLUDED_KEYS.has(key)) {
        return;
      }

      collectHospitalityStrings(nestedValue, target);
    });
  }
}

function getHospitalitySearchFields(property: Property): string[] {
  const hospitalityDetails = property.hospitalityDetails;
  if (!hospitalityDetails) {
    return [];
  }

  const fields: string[] = [];
  collectHospitalityStrings(hospitalityDetails, fields);

  return fields;
}

// Method to extract street, zipcode, and city from a full address string.
export function splitAddress(address: string): { street: string; zipcode: string; city: string } {
  const trimmed = (address ?? '').trim();
  if (!trimmed) return { street: '', zipcode: '', city: '' };

  // This regex matches the tail part: starts with a ZIP, then optional city, then optional repetitions.
  // We use [\p{L}] lookahead to ensure at least one city name exists in the tail.
  const TAIL_REGEX = /^(?=.*[\p{L}])\d{5}(?:\s+[\p{L}\s'-]+)?(?:\s*[/,+]\s*\d{5}(?:\s+[\p{L}\s'-]+)?)*$/u;

  // Find all comma indices to test potential split points
  const commaIndices: number[] = [];
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] === ',') {
      commaIndices.push(i);
    }
  }

  let street = trimmed;
  let rest = '';
  let found = false;

  // Iterate through commas from left to right to find the first one that starts a valid tail
  for (const idx of commaIndices) {
    const potentialStreet = trimmed.slice(0, idx).trim();
    const potentialTail = trimmed.slice(idx + 1).trim();

    if (TAIL_REGEX.test(potentialTail)) {
      street = potentialStreet;
      rest = potentialTail;
      found = true;
      break;
    }
  }

  if (!found) {
    return { street: trimmed, zipcode: '', city: '' };
  }

  // Find all 5-digit ZIP codes in the rest
  const zipCodes = rest.match(/\b\d{5}\b/g) || [];

  // Remove ZIP codes from rest to find city/cities
  let cityPart = rest;
  zipCodes.forEach(zip => {
    cityPart = cityPart.replace(zip, '');
  });

  // Clean up separators and extra whitespace
  const city = cityPart
    .split(/[\/,+]/)
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .filter((v, i, a) => a.indexOf(v) === i) // unique
    .join(' / ');

  const zipcode = zipCodes
    .filter((v, i, a) => a.indexOf(v) === i) // unique
    .join(' / ');

  return { street, zipcode, city: city || '' };
}

// Helper function to format service type headers to readable format
export function formatServiceTypeHeader(serviceKey: string): string {
  return serviceKey
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function normalizeServiceTypeKey(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function matchesSearchFields(
  searchTerm: string,
  textFields: Array<string | null | undefined>,
  phoneFields: Array<string | null | undefined>
): boolean {
  const normalizedTerm = normalizeSearchText(searchTerm);
  const tokens = normalizedTerm.split(' ').filter(Boolean);

  if (!tokens.length) {
    return true;
  }

  const combinedText = textFields
    .map(normalizeSearchText)
    .filter(Boolean)
    .join(' ');
  const normalizedPhones = phoneFields
    .map(normalizePhoneSearchValue)
    .filter(Boolean);

  return tokens.every((token) => {
    if (combinedText.includes(token)) {
      return true;
    }

    const normalizedPhoneToken = normalizePhoneSearchValue(token);
    return !!normalizedPhoneToken && normalizedPhones.some((phone) => phone.includes(normalizedPhoneToken));
  });
}

function addVisibleEmployeeFields(
  employees: Employee[] | null | undefined,
  visibleFields: VisibleEmployeeSearchField[] | undefined,
  textFields: Array<string | null | undefined>,
  phoneFields: Array<string | null | undefined>
): void {
  if (!employees?.length || !visibleFields?.length) {
    return;
  }

  const employee = employees[0];
  visibleFields.forEach((field) => {
    if (field === 'phone' || field === 'mobile') {
      phoneFields.push(employee[field]);
      return;
    }

    textFields.push(employee[field]);
  });
}

function addVisibleServiceFields(
  property: Property,
  serviceTypes: string[] | undefined,
  textFields: Array<string | null | undefined>,
  phoneFields: Array<string | null | undefined>
): void {
  if (!serviceTypes?.length) {
    return;
  }

  const services = Array.isArray(property.technicalServices) ? property.technicalServices : [];
  serviceTypes.forEach((serviceType) => {
    const normalizedType = normalizeServiceTypeKey(serviceType);
    const displayedService = services.find((service) =>
      normalizeServiceTypeKey(service?.serviceType) === normalizedType
    );

    if (!displayedService) {
      return;
    }

    textFields.push(displayedService.serviceName);
    phoneFields.push(displayedService.phone);
  });
}

function getLegacySearchFields(property: Property): Array<string | null | undefined> {
  const addressParts = splitAddress(property.address);
  return [
    property.address,
    addressParts.street,
    addressParts.zipcode,
    addressParts.city,
    property.caretaker?.name,
    property.caretaker?.email,
    property.caretaker?.phone,
    ...(property.propertyManagers || []).map(manager => manager.name),
    ...(property.technicalAssetsManagers || []).map(manager => manager.name),
    ...(property.sevManagers || []).map(manager => manager.name),
    ...(property.accountants || []).map(accountant => accountant.name),
    ...(property.technicalServices || []).map(service => service.serviceName),
    ...getHospitalitySearchFields(property),
    ...(property.externalReferences || [])
      .filter(ref => ref.provider === 'haussoft' && ref.externalId)
      .map(ref => ref.externalId),
  ];
}

export function isPropertyMatch(
  property: Property,
  searchTerm: string,
  configuration?: PropertyListSearchConfiguration
): boolean {
  if (!configuration || configuration.hospitalityMode) {
    return matchesSearchFields(searchTerm, getLegacySearchFields(property), []);
  }

  const addressParts = splitAddress(property.address);
  const textFields: Array<string | null | undefined> = [
    property.address,
    addressParts.street,
    addressParts.zipcode,
    addressParts.city,
    property.caretaker?.name,
    property.caretaker?.email,
    property.caretaker?.address,
  ];
  const phoneFields: Array<string | null | undefined> = [
    property.caretaker?.phone,
  ];

  if (configuration.includeHausCode) {
    textFields.push(
      ...(property.externalReferences || [])
        .filter(ref => ref.provider === 'haussoft' && ref.externalId)
        .map(ref => ref.externalId)
    );
  }

  addVisibleEmployeeFields(
    property.propertyManagers,
    configuration.propertyManagerFields,
    textFields,
    phoneFields
  );
  addVisibleEmployeeFields(
    property.technicalAssetsManagers,
    configuration.technicalAssetsManagerFields,
    textFields,
    phoneFields
  );
  addVisibleEmployeeFields(property.sevManagers, configuration.sevManagerFields, textFields, phoneFields);
  addVisibleEmployeeFields(property.accountants, configuration.accountantFields, textFields, phoneFields);
  addVisibleServiceFields(property, configuration.technicalServiceTypes, textFields, phoneFields);

  return matchesSearchFields(searchTerm, textFields, phoneFields);
}
